import { useCallback, useMemo } from 'react';

import { useTakeoverPilotExperiment } from 'bundles/search-common/experiments/takeoverPilot/TakeoverPilotExperimentProvider';
import type { TakeoverMatch } from 'bundles/search-common/experiments/takeoverPilot/getTakeoverMatchForQuery';
import { getTakeoverMatchForQuery } from 'bundles/search-common/experiments/takeoverPilot/getTakeoverMatchForQuery';
import { isTakeoverMatch } from 'bundles/search-common/experiments/takeoverPilot/isTakeoverMatch';
import {
  getTakeoverCardPosition,
  getTakeoverUpsellHit,
  insertHitAtIndex,
  repositionHitToIndex,
} from 'bundles/search-common/experiments/takeoverPilot/takeoverPilotHitUtils';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';

type UseTakeoverPilotHitPlacementArgs = {
  query: string | null | undefined;
  page: number;
  isMobileView: boolean;

  isDefaultUpsellSlotClaimed: boolean;
};

export type TakeoverPilotHitPlacement = {
  // Feed directly into SearchPageBody's getCombinedHitsForRender call.
  paginatedSlot: {
    showUpsellCard: boolean;
    upsellHitOverride: SearchUpsellHit | undefined;
  };

  applyToPaginatedHits: (hits: Array<SearchHit | SearchUpsellHit>) => Array<SearchHit | SearchUpsellHit>;

  applyToInfiniteHits: (hits: Array<SearchHit | SearchUpsellHit>) => Array<SearchHit | SearchUpsellHit>;

  takeoverMatch: TakeoverMatch | null;
};

export const useTakeoverPilotHitPlacement = ({
  query,
  page,
  isMobileView,
  isDefaultUpsellSlotClaimed,
}: UseTakeoverPilotHitPlacementArgs): TakeoverPilotHitPlacement => {
  const isInTreatment = useTakeoverPilotExperiment();
  // getTakeoverMatchForQuery rebuilds the campaign map on every call, so memoize on the actual
  // inputs -- otherwise takeoverMatch is a new reference every render, invalidating every
  // useMemo/useCallback below (and the object this hook itself returns) on every render.
  const takeoverMatch = useMemo(() => (isInTreatment ? getTakeoverMatchForQuery(query) : null), [isInTreatment, query]);
  const takeoverSlotIndex = getTakeoverCardPosition(isMobileView);

  const shouldClaimPaginatedSlot = Boolean(takeoverMatch) && page === 1 && !isDefaultUpsellSlotClaimed;
  const isInsertModeOnPaginatedPath = shouldClaimPaginatedSlot && takeoverMatch?.mode === 'insert';
  const isMatchModeOnPaginatedPath = shouldClaimPaginatedSlot && takeoverMatch?.mode === 'match';

  const paginatedSlot = useMemo(
    () => ({
      showUpsellCard: isDefaultUpsellSlotClaimed || isInsertModeOnPaginatedPath,
      upsellHitOverride: isInsertModeOnPaginatedPath && takeoverMatch ? getTakeoverUpsellHit(takeoverMatch) : undefined,
    }),
    [isDefaultUpsellSlotClaimed, isInsertModeOnPaginatedPath, takeoverMatch]
  );

  const applyToPaginatedHits = useCallback(
    (hits: Array<SearchHit | SearchUpsellHit>) =>
      isMatchModeOnPaginatedPath && takeoverMatch
        ? repositionHitToIndex(hits, takeoverSlotIndex, (hit) => isTakeoverMatch(hit, takeoverMatch))
        : hits,
    [isMatchModeOnPaginatedPath, takeoverMatch, takeoverSlotIndex]
  );

  const applyToInfiniteHits = useCallback(
    (hits: Array<SearchHit | SearchUpsellHit>) => {
      if (!takeoverMatch || page !== 1) {
        return hits;
      }
      return takeoverMatch.mode === 'insert'
        ? insertHitAtIndex(hits, takeoverSlotIndex, getTakeoverUpsellHit(takeoverMatch))
        : repositionHitToIndex(hits, takeoverSlotIndex, (hit) => isTakeoverMatch(hit, takeoverMatch));
    },
    [takeoverMatch, page, takeoverSlotIndex]
  );

  return useMemo(
    () => ({ paginatedSlot, applyToPaginatedHits, applyToInfiniteHits, takeoverMatch }),
    [paginatedSlot, applyToPaginatedHits, applyToInfiniteHits, takeoverMatch]
  );
};

export default useTakeoverPilotHitPlacement;
